import {
  Headline3,
  Label1,
  Label2,
  Icon,
} from "@bms-ai/bms-ai-ui-lib/components";
import type { StoryboardFrame } from "../../context/MultipleFileUploadContext";
import { useAppContext } from "../../context/app-context";
import { useState, useEffect, useMemo } from "react";
import { useCustomToast } from "../common/toast";
import "./StoryboardPreview.scss";
import { formatChannelValue } from "../../helpers/helpers";

interface StoryboardPreviewProps {
  previewData: StoryboardFrame[] | null;
  onFrameSelectionChange?: (selectedFrames: StoryboardFrame[]) => void;
}

interface DimensionGroup {
  dimension: string;
  frames: StoryboardFrame[];
  isSelected: boolean;
}

const StoryboardPreview: React.FC<StoryboardPreviewProps> = ({
  previewData,
  onFrameSelectionChange,
}) => {
  const { selectedChannel } = useAppContext();
  const { showToast } = useCustomToast();
  const [selectedDimensions, setSelectedDimensions] = useState<Set<string>>(
    new Set(),
  );
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(
    new Set(),
  );
  const [totalSelectedFrames, setTotalSelectedFrames] = useState(0);

  const isAnimatedBanner =
    formatChannelValue(selectedChannel?.label || "") === "animated_banner";
  // Group frames by dimension and sort by dimension size (ascending)
  const dimensionGroups = useMemo(() => {
    if (!previewData || previewData.length === 0) return [];

    const groupMap = new Map<string, StoryboardFrame[]>();
    previewData.forEach((frame) => {
      const dim = frame.dimension || "Unknown";
      if (!groupMap.has(dim)) {
        groupMap.set(dim, []);
      }
      groupMap.get(dim)!.push(frame);
    });

    // Convert to array and sort by dimension (ascending order)
    const groups: DimensionGroup[] = Array.from(groupMap.entries())
      .sort((a, b) => {
        // Extract numeric values from dimensions like "300x400"
        const getSize = (dim: string) => {
          const match = dim.match(/(\d+)/);
          return match ? parseInt(match[1]) : 0;
        };
        return getSize(a[0]) - getSize(b[0]);
      })
      .map(([dimension, frames]) => ({
        dimension,
        frames: frames.sort((a, b) => a.page - b.page),
        isSelected: false,
      }));

    return groups;
  }, [previewData]);

  // Default selection logic
  useEffect(() => {
    if (!isAnimatedBanner || dimensionGroups.length === 0) return;

    const totalFrames = previewData?.length || 0;
    const maxFrames = 20;

    if (totalFrames <= maxFrames) {
      // Select all dimensions if total frames <= 20
      const allDimensions = new Set(dimensionGroups.map((g) => g.dimension));
      setSelectedDimensions(allDimensions);
      setTotalSelectedFrames(totalFrames);
    } else {
      // Select from low to high dimension until we reach ~20 frames
      let frameCount = 0;
      const selectedDims = new Set<string>();

      for (const group of dimensionGroups) {
        if (frameCount + group.frames.length <= maxFrames) {
          selectedDims.add(group.dimension);
          frameCount += group.frames.length;
        } else {
          break;
        }
      }

      setSelectedDimensions(selectedDims);
      setTotalSelectedFrames(frameCount);
    }
  }, [isAnimatedBanner, dimensionGroups, previewData]);

  // Notify parent when selection changes
  useEffect(() => {
    if (!isAnimatedBanner || !onFrameSelectionChange) return;

    const selectedFrames = dimensionGroups
      .filter((g) => selectedDimensions.has(g.dimension))
      .flatMap((g) => g.frames);

    onFrameSelectionChange(selectedFrames);
  }, [
    selectedDimensions,
    isAnimatedBanner,
    onFrameSelectionChange,
    dimensionGroups,
  ]);

  const handleDimensionToggle = (dimension: string) => {
    const newSelected = new Set(selectedDimensions);
    const group = dimensionGroups.find((g) => g.dimension === dimension);
    if (!group) return;

    if (newSelected.has(dimension)) {
      // Check if this is the last selected dimension
      // if (newSelected.size === 1) {
      //   showToast("At least one section must be selected.", 5000);
      //   return;
      // }
      // Unselect the dimension
      newSelected.delete(dimension);
      setTotalSelectedFrames((prev) => prev - group.frames.length);
    } else {
      // Try to select the dimension
      const newCount = totalSelectedFrames + group.frames.length;
      if (newCount > 20) {
        // Can't select - show warning
        showToast(
          "This size cannot be selected since maximum 20 frames are allowed. Please unselect another size and select sizes adding up to a maximum of 20 frames.",
          5000,
        );
        return;
      }
      newSelected.add(dimension);
      setTotalSelectedFrames(newCount);
    }

    setSelectedDimensions(newSelected);
  };

  const handleDimensionExpandToggle = (dimension: string) => {
    const newExpanded = new Set(expandedDimensions);
    if (newExpanded.has(dimension)) {
      newExpanded.delete(dimension);
    } else {
      newExpanded.add(dimension);
    }
    setExpandedDimensions(newExpanded);
  };

  if (!previewData || previewData.length === 0) {
    return (
      <div className="preview-section">
        <div className="preview-area preview-area-filled">
          <div className="preview-images-container">
            <Label1 className="no-preview-text">
              No preview available for the uploaded storyboard.
            </Label1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="preview-section">
      <div className="preview-area preview-area-filled">
        <Label2 className="preview-image-count">
          {previewData.length} {previewData.length === 1 ? "frame" : "frames"}{" "}
          found
          {isAnimatedBanner && (
            <span className="selected-count">
              ({totalSelectedFrames} selected)
            </span>
          )}
        </Label2>

        {isAnimatedBanner ? (
          // Animated Banner: Dimension-based selection
          <div className="preview-images-container-animated">
            {dimensionGroups.map((group) => (
              <div key={group.dimension} className="dimension-section">
                <div className="dimension-header">
                  <div
                    className="dimension-checkbox-container"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDimensionToggle(group.dimension);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDimensions.has(group.dimension)}
                      onChange={() => {}} // Handled by parent div onClick
                      className="dimension-checkbox"
                    />
                  </div>
                  <div
                    className="dimension-info"
                    onClick={() => handleDimensionExpandToggle(group.dimension)}
                  >
                    <div className="dimension-text-wrapper">
                      <span className="dimension-name">{group.dimension}</span>
                      <span className="dimension-count">
                        ({group.frames.length}{" "}
                        {group.frames.length === 1 ? "frame" : "frames"})
                      </span>
                    </div>
                    <Icon
                      type={
                        expandedDimensions.has(group.dimension)
                          ? "chevron-up"
                          : "chevron-down"
                      }
                      size="small"
                      className="dimension-expand-icon"
                    />
                  </div>
                </div>

                {expandedDimensions.has(group.dimension) && (
                  <div className="dimension-frames">
                    {group.frames.map((frame) => {
                      const imgUrl = (frame as any).imageUrl;
                      return (
                        <div
                          key={`storyboard-${frame.frame_id}`}
                          className="preview-image-wrapper"
                        >
                          <Headline3 className="image-label">
                            Page {frame.page}
                          </Headline3>
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={`Storyboard Frame ${frame.page}`}
                              className="storyboard-image-reveal"
                            />
                          ) : (
                            <div className="frame-placeholder">
                              Frame ID: {frame.frame_id}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Non-animated banner: Show all frames without selection
          <div className="preview-images-container">
            {previewData.map((frame) => {
              const imgUrl = (frame as any).imageUrl;
              return (
                <div
                  key={`storyboard-${frame.frame_id}`}
                  className="preview-image-wrapper"
                >
                  <Headline3 className="image-label">
                    Page {frame.page}
                  </Headline3>
                  {frame.dimension && (
                    <Label2 className="image-metadata">
                      {frame.dimension || "Size info pending"}
                    </Label2>
                  )}
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt={`Storyboard Frame ${frame.page}`}
                      className="storyboard-image-reveal"
                    />
                  ) : (
                    <div className="frame-placeholder">
                      Frame ID: {frame.frame_id}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryboardPreview;
